import { useEffect, useMemo, useRef, useState } from 'react';

function chipElement(reference, onRemove, onHover) {
  const kind = reference.kind || 'image';
  const kindLabel = kind === 'video' ? '视频' : kind === 'audio' ? '音频' : '图片';
  const token = `@${kindLabel}${reference.sequence || 1}`;
  const chip = document.createElement('span');
  chip.className = 'canvas-inline-ref';
  chip.contentEditable = 'false';
  chip.dataset.referenceId = reference.id;
  chip.dataset.token = token;
  chip.title = reference.name || token;
  if ((kind === 'image' || reference.previewUrl) && (reference.previewUrl || reference.url)) {
    const image = document.createElement('img');
    image.src = reference.previewUrl || reference.url;
    image.alt = '';
    image.draggable = false;
    chip.append(image);
  } else {
    const marker = document.createElement('i');
    marker.textContent = kindLabel.toUpperCase();
    marker.className = `canvas-inline-ref__marker canvas-inline-ref__marker--${kind}`;
    chip.append(marker);
  }
  const label = document.createElement('b');
  label.textContent = `${kindLabel}${reference.sequence || 1}`;
  chip.append(label);
  const remove = document.createElement('button');
  remove.type = 'button';
  remove.tabIndex = -1;
  remove.setAttribute('aria-label', `移除${kindLabel}${reference.sequence || 1}标记`);
  remove.innerHTML = '&times;';
  remove.addEventListener('pointerdown', (event) => event.preventDefault());
  remove.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    onRemove(reference.id, chip);
  });
  chip.addEventListener('mouseenter', () => onHover(reference.id));
  chip.addEventListener('mouseleave', () => onHover(''));
  chip.append(remove);
  return chip;
}

function editorPrompt(root) {
  const read = (node) => {
    if (node.nodeType === Node.TEXT_NODE) return node.textContent || '';
    if (!(node instanceof HTMLElement)) return '';
    if (node.matches('[data-reference-id]')) return node.dataset.token || '';
    if (node.tagName === 'BR') return '\n';
    const result = [...node.childNodes].map(read).join('');
    return ['DIV', 'P'].includes(node.tagName) ? `${result}\n` : result;
  };
  return [...(root?.childNodes || [])].map(read).join('').replace(/\u00a0/g, ' ').replace(/\n{3,}/g, '\n\n').trimEnd();
}

function mentionRangeAtCaret(root) {
  const selection = window.getSelection();
  if (!root || !selection?.rangeCount) return null;
  const caretRange = selection.getRangeAt(0);
  if (!caretRange.collapsed || !root.contains(caretRange.startContainer)) return null;

  let textNode = caretRange.startContainer;
  let caret = caretRange.startOffset;
  if (textNode.nodeType !== Node.TEXT_NODE) {
    const children = [...textNode.childNodes].slice(0, caret).reverse();
    textNode = children.find((node) => node.nodeType === Node.TEXT_NODE) || null;
    if (!textNode) return null;
    caret = textNode.textContent?.length || 0;
  }
  const text = textNode.textContent || '';
  const match = text.slice(0, caret).match(/@([^\s@]*)$/u);
  if (!match) return null;
  const range = document.createRange();
  range.setStart(textNode, caret - match[0].length);
  range.setEnd(textNode, caret);
  const path = [];
  let cursor = textNode;
  while (cursor && cursor !== root) {
    const parent = cursor.parentNode;
    if (!parent) return null;
    path.unshift([...parent.childNodes].indexOf(cursor));
    cursor = parent;
  }
  return {
    range: range.cloneRange(),
    query: match[1] || '',
    path,
    startOffset: caret - match[0].length,
    endOffset: caret,
    token: match[0],
  };
}

function mentionRangeFromBookmark(root, bookmark) {
  if (!root || !bookmark) return null;
  const liveRange = bookmark.range;
  if (liveRange?.startContainer?.isConnected && root.contains(liveRange.startContainer)) {
    return liveRange.cloneRange();
  }
  let node = root;
  for (const index of bookmark.path || []) node = node?.childNodes?.[index];
  if (node?.nodeType !== Node.TEXT_NODE) return null;
  const text = node.textContent || '';
  const start = Math.min(text.length, Math.max(0, Number(bookmark.startOffset) || 0));
  const end = Math.min(text.length, Math.max(start, Number(bookmark.endOffset) || start));
  if (bookmark.token && text.slice(start, end) !== bookmark.token) return null;
  const range = document.createRange();
  range.setStart(node, start);
  range.setEnd(node, end);
  return range;
}

function adjacentInlineChip(root, range, direction) {
  if (!root || !range?.collapsed || !root.contains(range.startContainer)) return null;
  const container = range.startContainer;
  const isText = container.nodeType === Node.TEXT_NODE;
  const parent = isText ? container.parentNode : container;
  if (!parent || !root.contains(parent)) return null;
  const offset = Number(range.startOffset || 0);
  if (isText) {
    const length = container.textContent?.length || 0;
    if (direction < 0 ? offset !== 0 : offset !== length) return null;
  }
  const index = isText ? [...parent.childNodes].indexOf(container) : offset;
  const candidateIndex = direction < 0 ? index - 1 : index + (isText ? 1 : 0);
  const candidate = parent.childNodes[candidateIndex];
  if (!(candidate instanceof HTMLElement) || !candidate.matches('[data-reference-id]')) return null;
  return { parent, index: candidateIndex, candidate };
}

function placeCaret(parent, offset) {
  if (!parent) return;
  const selection = window.getSelection();
  const range = document.createRange();
  range.setStart(parent, Math.max(0, Math.min(parent.childNodes.length, offset)));
  range.collapse(true);
  selection?.removeAllRanges();
  selection?.addRange(range);
}

export default function ReferencePrompt({ value, options = [], onChange, onAdd, onMentionHover, placeholder, height = 92, onHeightChange, maxLength = null }) {
  const [mentionOpen, setMentionOpen] = useState(false);
  const [query, setQuery] = useState('');
  const editorRef = useRef(null);
  const mentionRangeRef = useRef(null);
  const resizeRef = useRef(null);
  const optionsRef = useRef(options);
  const [promptHeight, setPromptHeight] = useState(Math.min(300, Math.max(60, Number(height) || 92)));
  const available = useMemo(() => options.filter((item) => {
    const kindLabel = item.kind === 'video' ? '视频' : item.kind === 'audio' ? '音频' : '图片';
    const label = `${kindLabel}${item.sequence || 1} ${item.name}`;
    return !query || label.toLowerCase().includes(query.toLowerCase());
  }), [options, query]);

  useEffect(() => { optionsRef.current = options; }, [options]);
  useEffect(() => { setPromptHeight(Math.min(300, Math.max(60, Number(height) || 92))); }, [height]);

  const limit = Number.isFinite(Number(maxLength)) && Number(maxLength) > 0 ? Number(maxLength) : null;
  const emit = () => {
    const raw = editorPrompt(editorRef.current);
    const next = limit ? Array.from(raw).slice(0, limit).join('') : raw;
    if (next !== raw) renderValue(next);
    onChange(next);
  };
  const removeChip = (id, chip) => {
    chip?.remove();
    emit();
    editorRef.current?.focus();
  };
  const renderValue = (nextValue) => {
    const root = editorRef.current;
    if (!root) return;
    root.replaceChildren();
    const bySequence = new Map(optionsRef.current.map((item) => {
      const kindLabel = item.kind === 'video' ? '视频' : item.kind === 'audio' ? '音频' : '图片';
      return [`${kindLabel}:${Number(item.sequence || 1)}`, item];
    }));
    const source = String(nextValue || '');
    const expression = /@(图片|视频|音频)(\d+)/gu;
    let cursor = 0;
    for (const match of source.matchAll(expression)) {
      if (match.index > cursor) root.append(document.createTextNode(source.slice(cursor, match.index)));
      const reference = bySequence.get(`${match[1]}:${Number(match[2])}`);
      if (reference) root.append(chipElement(reference, removeChip, (id) => onMentionHover?.(id)));
      cursor = match.index + match[0].length;
    }
    if (cursor < source.length) root.append(document.createTextNode(source.slice(cursor)));
  };

  useEffect(() => {
    const next = String(value || '');
    if (document.activeElement !== editorRef.current || editorPrompt(editorRef.current) !== next) renderValue(next);
  // Re-render when connected references change so sequence and thumbnails stay correct.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, options]);

  const syncMentionMenu = () => {
    const root = editorRef.current;
    const candidate = mentionRangeAtCaret(root);
    if (!candidate) {
      setMentionOpen(false);
      mentionRangeRef.current = null;
      return;
    }
    mentionRangeRef.current = candidate;
    setQuery(candidate.query);
    setMentionOpen(true);
  };

  const insertReference = (item, explicitBookmark = mentionRangeRef.current) => {
    const root = editorRef.current;
    if (!root) return;
    root.focus();
    const selection = window.getSelection();
    const bookmarkedRange = mentionRangeFromBookmark(root, explicitBookmark);
    const range = bookmarkedRange || document.createRange();
    if (!bookmarkedRange) {
      range.selectNodeContents(root);
      range.collapse(false);
    }
    range.deleteContents();
    const chip = chipElement(item, removeChip, (id) => onMentionHover?.(id));
    const spacer = document.createTextNode('\u00a0');
    range.insertNode(spacer);
    range.insertNode(chip);
    range.setStartAfter(spacer);
    range.collapse(true);
    selection?.removeAllRanges();
    selection?.addRange(range);
    onAdd(item.id);
    emit();
    mentionRangeRef.current = null;
    setMentionOpen(false);
    setQuery('');
  };

  const startResize = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const startY = event.clientY;
    const startHeight = promptHeight;
    const move = (moveEvent) => {
      const next = Math.min(300, Math.max(60, startHeight + (moveEvent.clientY - startY)));
      resizeRef.current = next;
      setPromptHeight(next);
    };
    const stop = () => {
      window.removeEventListener('pointermove', move);
      onHeightChange?.(Number(resizeRef.current) || startHeight);
    };
    resizeRef.current = startHeight;
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', stop, { once: true });
  };

  return <div className="canvas-prompt nodrag nowheel">
    <div className="canvas-prompt__surface">
      {options.length > 0 && <div className="canvas-reference-strip" aria-label="全局已连接参考素材">
        {options.map((reference) => { const kindLabel = reference.kind === 'video' ? '视频' : reference.kind === 'audio' ? '音频' : '图片'; return <button key={reference.id} type="button" title={`${kindLabel}${reference.sequence || 1} · ${reference.name}`} onClick={() => insertReference(reference, null)} onMouseEnter={() => onMentionHover?.(reference.id)} onMouseLeave={() => onMentionHover?.('')}>
          {(reference.kind === 'image' || reference.previewUrl) && (reference.previewUrl || reference.url) ? <img src={reference.previewUrl || reference.url} alt="" loading="lazy" decoding="async" /> : <i className={`canvas-reference-marker canvas-reference-marker--${reference.kind || 'image'}`}>{kindLabel.slice(0, 1)}</i>}
          <b>{kindLabel}{reference.sequence || 1}</b>
        </button>; })}
      </div>}
      <div ref={editorRef} className="canvas-prompt__editor" contentEditable suppressContentEditableWarning role="textbox" aria-multiline="true" data-placeholder={placeholder} style={{ height: `${promptHeight}px` }} onInput={() => { emit(); window.requestAnimationFrame(syncMentionMenu); }} onClick={syncMentionMenu} onKeyUp={syncMentionMenu} onFocus={syncMentionMenu} onBlur={() => setMentionOpen(false)} onKeyDown={(event) => {
        if (event.key === 'Backspace' || event.key === 'Delete') {
          const selection = window.getSelection();
          const adjacent = adjacentInlineChip(editorRef.current, selection?.rangeCount ? selection.getRangeAt(0) : null, event.key === 'Backspace' ? -1 : 1);
          if (adjacent) {
            event.preventDefault();
            adjacent.candidate.remove();
            placeCaret(adjacent.parent, adjacent.index < 0 ? 0 : adjacent.index);
            emit();
            setMentionOpen(false);
            return;
          }
        }
        if (event.key === 'Escape') setMentionOpen(false);
        if (event.key === 'Enter' && mentionOpen && available[0]) {
          event.preventDefault();
          insertReference(available[0]);
        }
      }} />
      {limit && <div className={`canvas-prompt__counter ${Array.from(String(value || '')).length > limit ? 'is-over' : ''}`}>{Array.from(String(value || '')).length}/{limit}</div>}
      <div className="canvas-prompt__resize-handle" role="separator" aria-label="调整提示词高度" onPointerDown={startResize}><span /></div>
    </div>
    {mentionOpen && <div className="canvas-mention-menu">
      {available.length ? available.slice(0, 8).map((item) => <button type="button" key={item.id} onMouseDown={(event) => event.preventDefault()} onClick={() => insertReference(item)} onMouseEnter={() => onMentionHover?.(item.id)} onMouseLeave={() => onMentionHover?.('')}>
        {(item.kind === 'image' || item.previewUrl) && (item.previewUrl || item.url) ? <img src={item.previewUrl || item.url} alt="" loading="lazy" decoding="async" /> : <i className={`canvas-mention-marker canvas-mention-marker--${item.kind || 'image'}`}>{item.kind === 'video' ? 'V' : item.kind === 'audio' ? 'A' : 'I'}</i>}
        <span><strong>{item.kind === 'video' ? '视频' : item.kind === 'audio' ? '音频' : '图片'}{item.sequence || 1} · {item.name}</strong><small>插入光标位置</small></span>
      </button>) : <p>请先连接参考素材</p>}
    </div>}
  </div>;
}
